// Tally keymap — the default for migrating accountants.
// Every entry here is BOUND: either a global handler in <App /> or a screen-level
// handler (voucher entry forms bind F2/F3/Ctrl+A themselves). Do not add a binding
// without wiring its handler — the Help page renders from real bindings only.
//
// Source: TALLY_SHORTCUTS.md (read for the complete mapping).
// HARD RULE (CLAUDE.md): every binding sets preventDefault so browser defaults (F5, Ctrl+P, F11)
// are overridden — required for Tally-keyboard parity.

import type { Keymap } from './types';

export const tallyKeymap: Keymap = [
  // ─── Window / global ──────────────────────────────────────────────
  {
    id: 'window.help',
    label: 'Help',
    group: 'Window',
    combos: ['f1'],
    scope: '*',
  },
  {
    id: 'window.date',
    label: 'Change date (voucher entry)',
    group: 'Window',
    combos: ['f2'],
    scope: '*',
    showInFooter: false,
  },
  {
    id: 'window.party',
    label: 'Focus party (voucher entry)',
    group: 'Window',
    combos: ['f3'],
    scope: '*',
    showInFooter: false,
  },
  {
    id: 'window.escape',
    label: 'Go back',
    group: 'Window',
    combos: ['escape'],
    scope: '*',
    showInFooter: false,
  },

  // ─── Navigation ──────────────────────────────────────────────────
  // Single canonical palette shortcut: Ctrl+K (matches Linear / Notion / Slack).
  {
    id: 'nav.command-palette',
    label: 'Command palette',
    group: 'Navigation',
    combos: ['ctrl+k'],
    scope: '*',
  },
  {
    id: 'nav.gateway',
    label: 'Gateway',
    group: 'Navigation',
    combos: ['alt+f3'],
    scope: '*',
    showInFooter: false,
  },

  // ─── Vouchers (Tally voucher-type keys, work from any screen) ─────
  { id: 'voucher.new.contra',      label: 'Contra',      group: 'Vouchers', combos: ['f4'], scope: '*', showInFooter: false },
  { id: 'voucher.new.payment',     label: 'Payment',     group: 'Vouchers', combos: ['f5'], scope: '*', showInFooter: false },
  { id: 'voucher.new.receipt',     label: 'Receipt',     group: 'Vouchers', combos: ['f6'], scope: '*', showInFooter: false },
  { id: 'voucher.new.journal',     label: 'Journal',     group: 'Vouchers', combos: ['f7'], scope: '*', showInFooter: false },
  { id: 'voucher.new.sales',       label: 'Sales',       group: 'Vouchers', combos: ['f8'], scope: '*' },
  { id: 'voucher.new.purchase',    label: 'Purchase',    group: 'Vouchers', combos: ['f9'], scope: '*' },
  { id: 'voucher.new.debit-note',  label: 'Debit Note',  group: 'Vouchers', combos: ['alt+f5'], scope: '*', showInFooter: false },
  { id: 'voucher.new.credit-note', label: 'Credit Note', group: 'Vouchers', combos: ['alt+f6'], scope: '*', showInFooter: false },
  { id: 'voucher.list',            label: 'All vouchers', group: 'Vouchers', combos: ['f10'], scope: '*', showInFooter: false },

  // ─── Reports ──────────────────────────────────────────────────────
  { id: 'report.day-book',          label: 'Day Book',          group: 'Reports', combos: ['alt+k'], scope: '*', showInFooter: false },
  { id: 'report.trial-balance',     label: 'Trial Balance',     group: 'Reports', combos: ['alt+t'], scope: '*', showInFooter: false },
  { id: 'report.pnl',               label: 'Profit & Loss',     group: 'Reports', combos: ['alt+l'], scope: '*', showInFooter: false },
  { id: 'report.balance-sheet',     label: 'Balance Sheet',     group: 'Reports', combos: ['alt+b'], scope: '*', showInFooter: false },
  { id: 'report.cash-flow',         label: 'Cash Flow',         group: 'Reports', combos: ['alt+c'], scope: '*', showInFooter: false },
  { id: 'report.outstanding',       label: 'Outstanding',       group: 'Reports', combos: ['alt+o'], scope: '*', showInFooter: false },
  { id: 'report.party-statement',   label: 'Party Statement',   group: 'Reports', combos: ['alt+y'], scope: '*', showInFooter: false },
  { id: 'report.group-summary',     label: 'Group Summary',     group: 'Reports', combos: ['alt+j'], scope: '*', showInFooter: false },
  { id: 'report.stock-summary',     label: 'Stock Summary',     group: 'Reports', combos: ['alt+s'], scope: '*', showInFooter: false },
  { id: 'report.reorder',           label: 'Reorder Report',    group: 'Reports', combos: ['alt+r'], scope: '*', showInFooter: false },
  { id: 'report.msme',              label: 'MSME 43B(h)',       group: 'Reports', combos: ['alt+m'], scope: '*', showInFooter: false },
  { id: 'report.tds',               label: 'TDS Liability',     group: 'Reports', combos: ['alt+d'], scope: '*', showInFooter: false },
  { id: 'report.gstr-1',            label: 'GSTR-1',            group: 'Reports', combos: ['alt+g'], scope: '*', showInFooter: false },
  { id: 'report.gstr-3b',           label: 'GSTR-3B',           group: 'Reports', combos: ['alt+3'], scope: '*', showInFooter: false },
  { id: 'report.sales-register',    label: 'Sales Register',    group: 'Reports', combos: ['alt+shift+i'], scope: '*', showInFooter: false },
  { id: 'report.purchase-register', label: 'Purchase Register', group: 'Reports', combos: ['alt+u'], scope: '*', showInFooter: false },

  // ─── Masters ──────────────────────────────────────────────────────
  { id: 'master.parties',       label: 'Parties',       group: 'Masters', combos: ['alt+p'], scope: '*', showInFooter: false },
  { id: 'master.items',         label: 'Items',         group: 'Masters', combos: ['alt+i'], scope: '*', showInFooter: false },
  { id: 'master.ledgers',       label: 'Ledgers',       group: 'Masters', combos: ['alt+shift+l'], scope: '*', showInFooter: false },
  { id: 'master.voucher-types', label: 'Voucher Types', group: 'Masters', combos: ['alt+v'], scope: '*', showInFooter: false },
  { id: 'master.hsn',           label: 'HSN / SAC',     group: 'Masters', combos: ['alt+h'], scope: '*', showInFooter: false },

  // ─── Admin ────────────────────────────────────────────────────────
  { id: 'nav.admin', label: 'Tenant Portal', group: 'Navigation', combos: ['alt+a'], scope: '*', showInFooter: false },

  // ─── Edit ────────────────────────────────────────────────────────
  {
    id: 'edit.accept',
    label: 'Accept / Save',
    group: 'Edit',
    combos: ['ctrl+a'],
    scope: '*',
  },
  {
    id: 'edit.export',
    label: 'Export (CSV)',
    group: 'Edit',
    combos: ['alt+e'],
    scope: '*',
  },
  {
    id: 'edit.print',
    label: 'Print',
    group: 'Edit',
    combos: ['ctrl+p'],
    scope: '*',
  },
];
